import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function SignupStep4({ formData, onComplete, onBack }) {
  const [businessInfo, setBusinessInfo] = useState({
    name: '',
    yearsInBusiness: '',
    address: {
      city: '',
      state: '',
      zip: '',
      country: '',
    },
    location: {
      type: 'Point',
      coordinates: [],
      formattedAddress: '',
    },
  });
  const [statesList, setStatesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchStates = async () => {
      if (businessInfo.address.country === 'USA') {
        try {
          const res = await axios.get('/api/dropdowns/category/statesUSA');
          setStatesList(res.data.options || []);
        } catch (err) {
          console.error('Failed to load states list:', err);
        }
      } else {
        setStatesList([]);
      }
    };
    fetchStates();
  }, [businessInfo.address.country]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (['city', 'state', 'zip', 'country'].includes(name)) {
      setBusinessInfo((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [name]: value,
        },
      }));
    } else {
      setBusinessInfo((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const fullAddress = `${businessInfo.address.city}, ${businessInfo.address.state}, ${businessInfo.address.zip}, ${businessInfo.address.country}`;
      const geoRes = await axios.get('/api/utils/geocode', {
        params: { address: fullAddress },
      });

      const { coordinates, formattedAddress } = geoRes.data;
      const updatedBusinessInfo = {
        ...businessInfo,
        location: {
          type: 'Point',
          coordinates,
          formattedAddress,
        },
      };

      setBusinessInfo(updatedBusinessInfo);
      await onComplete(updatedBusinessInfo); // Save & go to next step
    } catch (err) {
      console.error('Geocoding or submission error:', err);
      setMessage('❌ Failed to save business info. Please check your address.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      <h2 className="text-xl font-semibold">Business Info (Optional)</h2>
      <p className="text-sm text-gray-500">You can skip this step and update your profile later.</p>

      <input
        type="text"
        name="name"
        placeholder="Farm/Business Name"
        value={businessInfo.name}
        onChange={handleChange}
        className="border p-2"
      />

      <input
        type="number"
        name="yearsInBusiness"
        placeholder="Years in Business"
        value={businessInfo.yearsInBusiness}
        onChange={handleChange}
        className="border p-2"
      />

      <input
        type="text"
        name="city"
        placeholder="City"
        value={businessInfo.address.city}
        onChange={handleChange}
        className="border p-2"
      />

      {businessInfo.address.country === 'USA' ? (
        <select
          name="state"
          value={businessInfo.address.state}
          onChange={handleChange}
          className="border p-2"
        >
          <option value="">Select State</option>
          {statesList.map((s, i) => (
            <option key={i} value={s}>{s}</option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          name="state"
          placeholder="State/Province"
          value={businessInfo.address.state}
          onChange={handleChange}
          className="border p-2"
        />
      )}

      <input
        type="text"
        name="zip"
        placeholder="Zip Code"
        value={businessInfo.address.zip}
        onChange={handleChange}
        className="border p-2"
      />

      <input
        type="text"
        name="country"
        placeholder="Country"
        value={businessInfo.address.country}
        onChange={handleChange}
        className="border p-2"
      />

      {message && <p className="text-sm text-center text-gray-600">{message}</p>}

      <div className="flex gap-4">
        <button type="button" onClick={onBack} className="bg-gray-300 px-4 py-2 rounded">Back</button>
        <button type="submit" disabled={loading} className="bg-blue-600 text-white px-4 py-2 rounded">
          {loading ? 'Saving...' : 'Save & Continue'}
        </button>
        <button type="button" onClick={() => onComplete(null)} className="text-blue-500 underline">Skip</button>
      </div>
    </form>
  );
}
